import { useRef } from 'react';
import { InfoTitle } from '../../../../components/Toolkit/InfoTitle/InfoTitle';
import { useFilter } from '../../../../features/filters/components/Filter/Filter.hook';
import { LocationFilter } from '../../../../features/filters/components/Location/Location';

import { useFiltersDispatch } from '../../../../features/filters/Filters.context';
import * as Styled from '../../../../features/filters/components/Filter/Filter.styled';
import { Spacer } from '../../../../helpers/genericStyles';
import type { GroupedFilterProps } from '../../../../features/filters/components/Filter/GroupedFilter.typed';
import { FiltersV2 } from '../../../../features/filters/FiltersV2';

import * as LocationStyled from '../../../../features/filters/Filters.styled';
import { GroupedFilterHeading } from './GroupedFilterHeading/GroupedFilterHeading';
import { shouldApplyGapBetweenCheckboxFilters } from '../../helpers/filterUtils';

const GroupedFilter = (props: GroupedFilterProps) => {
  const {
    item,
    locationConfiguration,
    makeModelConfiguration,
    validPriceConfiguration,
    sectionName,
    toggleAccordionState,
    isOpen,
    showDivider,
    subFilters,
  } = props;
  const { displayText } = useFiltersDispatch();
  const ref = useRef<HTMLDivElement>(null);

  const { selectedOptions, selectedOptionDisplayText } = useFilter({
    item,
    ref,
    displayText,
    subFilters,
  });

  return (
    <Styled.FilterListItem>
      <Styled.Accordion
        key={item.id}
        ref={ref}
        header={
          <InfoTitle
            title={
              <GroupedFilterHeading filter={item} sectionName={sectionName} />
            }
            description={
              selectedOptionDisplayText && (
                <Styled.Description
                  isOpen={isOpen}
                  hasDescription={!!selectedOptions.length}
                >
                  {selectedOptionDisplayText}
                </Styled.Description>
              )
            }
          />
        }
        headerSpacerBottom={<Spacer marginBottom={isOpen ? 'S8' : 'M16'} />}
        bodySpacer={<Spacer />}
        isOpen={isOpen}
        onChange={() => toggleAccordionState(item.name)}
        showDivider={showDivider}
        content={
          <Styled.FilterWrapper>
            {item.name === 'location' ? (
              <LocationStyled.LocationFilter>
                <LocationFilter
                  {...locationConfiguration}
                  displayLabel={false}
                  heightVariant="LARGE"
                />
              </LocationStyled.LocationFilter>
            ) : (
              subFilters &&
              subFilters.map((subFilter, index) => {
                const nextSubFilter = subFilters[index + 1];
                const isLastItem = index === subFilters.length - 1;
                const shouldHaveGap = shouldApplyGapBetweenCheckboxFilters(
                  subFilter,
                  nextSubFilter,
                  isLastItem,
                );

                return (
                  <Styled.SubFilter
                    key={subFilter.name}
                    id={`Subfilter-${subFilter.name}`}
                    gap={shouldHaveGap}
                  >
                    <FiltersV2
                      filter={{
                        ...subFilter,
                        showMoreLabel: subFilter.displayName,
                        // We do not want to display the 'show more' label for filters within accordions that are closed on initial render
                        defaultShouldShowMore: !isOpen,
                        onChange: subFilter.onChange,
                        value: subFilter.value,
                      }}
                      locationConfiguration={locationConfiguration}
                      makeModelConfiguration={makeModelConfiguration}
                      validPriceConfiguration={validPriceConfiguration}
                      sectionName={sectionName}
                    />
                  </Styled.SubFilter>
                );
              })
            )}
          </Styled.FilterWrapper>
        }
      />
    </Styled.FilterListItem>
  );
};

export { GroupedFilter };
